import React from "react";
import { Star } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

const Stars = ({ score, scale }) => {
  const outOf5 = (score / scale) * 5;
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={15} className={i < Math.round(outOf5) ? "fill-terracotta text-terracotta" : "text-ink-400/30"} />
      ))}
    </div>
  );
};

const Badge = ({ platform, data, t }) => {
  if (!data || data.score == null) return null;
  return (
    <div className="flex items-center gap-3.5 rounded-2xl bg-white px-5 py-4 border border-black/5 shadow-[0_8px_32px_rgba(44,48,48,0.06)]" data-testid={`social-proof-${platform}`}>
      <div className="flex flex-col items-center justify-center rounded-xl bg-brand text-white px-3 py-2 min-w-[58px]">
        <span className="font-heading text-2xl leading-none">{fmt(data.score)}</span>
        <span className="text-[10px] text-white/70 mt-0.5">{t("socialProof.outOf")} {data.scale}</span>
      </div>
      <div>
        <Stars score={data.score} scale={data.scale} />
        <div className="mt-1 text-sm">
          <span className="font-medium text-ink-900">{data.label || (platform === "booking" ? "Booking.com" : "Google")}</span>
        </div>
        <div className="text-xs text-ink-400">
          {platform === "booking" ? t("socialProof.booking") : t("socialProof.google")}
          {data.count ? ` · ${data.count} ${t("socialProof.reviews")}` : ""}
        </div>
      </div>
    </div>
  );
};

export const SocialProof = ({ compact = false }) => {
  const { t } = useI18n();
  const { settings } = useSite();
  const ratings = settings.ratings || {};
  if (!ratings.booking && !ratings.google) return null;

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-3" data-testid="social-proof-compact">
        <Badge platform="booking" data={ratings.booking} t={t} />
        <Badge platform="google" data={ratings.google} t={t} />
      </div>
    );
  }

  return (
    <section className="py-12 lg:py-16 bg-beige/60" data-testid="social-proof-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
        <h2 className="font-heading text-2xl sm:text-3xl text-ink-900">{t("socialProof.title")}</h2>
        <p className="text-ink-600 font-body text-sm mt-2 mb-8">{t("socialProof.subtitle")}</p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Badge platform="booking" data={ratings.booking} t={t} />
          <Badge platform="google" data={ratings.google} t={t} />
        </div>
      </div>
    </section>
  );
};
